/**
 * applyMergeConvert — commits a `convertMergeToUpsert` result by replacing the
 * whole MERGE statement's byte range with the generated upsert text, then
 * re-syncing the resolver so the VQE immediately re-parses and re-renders the
 * result in the first-class upsert editor (`OnConflictCard` / `OnDuplicateCard`).
 *
 * This is a THIN, engine-blind range-replace applier: it never decides SQL (the
 * pure `convertMergeToUpsert` transform already did), it just writes text. It
 * mirrors `commitEdit`'s Monaco apply tail (offset→position, `executeEdits`
 * under the shared `vqe-edit` undo source, then `service.onTextChange`) rather
 * than routing through a resolver-layer `StructuralEdit` op — the conversion is
 * a plain whole-statement rewrite, not a structural mutation the SR-Edit
 * planners model.
 *
 * Stale-buffer guard: like `commitEdit`, if the authoritative live buffer has
 * moved since the MERGE was rendered, the range offsets no longer line up — so
 * it throws rather than splice against a stale plan. The caller treats a throw
 * as "not applied" (no success analytics, Edit-as-SQL still available).
 */

import type { SqlRange } from '../../../../sql-grammar/model';
import { VQE_UNDO_SOURCE, rangeFromOffsets } from '../commitEdit';
import type { VqeEditContext } from '../editContext';

export interface ApplyMergeConvertParams {
  readonly editContext: VqeEditContext;
  /** The MERGE statement's range in `originalSource`. */
  readonly statementRange: SqlRange;
  /** The buffer the MERGE was rendered against (the range indexes into this). */
  readonly originalSource: string;
  /** The upsert SQL produced by `convertMergeToUpsert`. */
  readonly generatedSql: string;
}

export async function applyMergeConvert({
  editContext,
  statementRange,
  originalSource,
  generatedSql,
}: ApplyMergeConvertParams): Promise<void> {
  const { model, editor, service, documentId, getLiveSource } = editContext;

  if (getLiveSource && getLiveSource() !== originalSource) {
    throw new Error(
      'applyMergeConvert: the buffer changed before the conversion could apply — try again',
    );
  }

  const start = Math.max(0, statementRange.startOffset);
  const end = Math.min(originalSource.length, Math.max(start, statementRange.endOffset));
  const emitted = originalSource.slice(0, start) + generatedSql + originalSource.slice(end);

  const edit = { range: rangeFromOffsets(model, start, end), text: generatedSql };
  if (editor) editor.executeEdits(VQE_UNDO_SOURCE, [edit]);
  else model.pushEditOperations(null, [edit], () => null);

  await service.onTextChange(documentId, emitted);
}

